import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Megaphone,
  Plus,
  AlertCircle,
  RefreshCw,
  Loader2,
  Radio,
  CalendarClock,
  Trash2,
  Type,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import {
  useLineOABroadcastHistory,
  useLineOABroadcastQuota,
  useLineOAScheduledBroadcasts,
  useCancelScheduledBroadcast,
} from './useLineOABroadcast'
import { LineOAStoreSwitcher } from './LineOAStoreSwitcher'

function fmtDateTime(s?: string | null) {
  if (!s) return '—'
  return new Date(s.replace(' ', 'T')).toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function LineOABroadcastPage() {
  const { status, isLoading: isLoadingStatus } = useLineMessagingConfig()
  const connected = !!status?.connected
  const { data, isLoading: isLoadingHistory, isError, refetch } = useLineOABroadcastHistory({ enabled: connected })
  const { data: quota } = useLineOABroadcastQuota({ enabled: connected })
  const { data: scheduledData } = useLineOAScheduledBroadcasts({ enabled: connected })
  const cancelScheduled = useCancelScheduledBroadcast()

  if (isLoadingStatus) {
    return (
      <PageContainer>
        <div className="p-6 flex items-center justify-center min-h-[200px]">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    )
  }

  if (!connected) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <h2 className="text-2xl font-semibold">Broadcast</h2>
          <Card className="rounded-2xl border-0 max-w-lg">
            <CardContent className="p-6 space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-[#06C755]/10 flex items-center justify-center mx-auto">
                <Megaphone className="w-6 h-6 text-[#06C755]" />
              </div>
              <p className="font-medium">Connect LINE Messaging API to send broadcasts.</p>
              <Button asChild className="bg-[#06C755] hover:bg-[#06C755]/80">
                <Link to="/storefront/settings/line">Go to Settings</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    )
  }

  const broadcasts = data?.broadcasts ?? []
  const scheduled = scheduledData?.scheduled ?? []

  return (
    <PageContainer>
      <div className="p-6 max-w-5xl space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-[1.6rem] font-semibold text-[#353a40]">Broadcast</h2>
            <p className="text-sm text-black/50 mt-0.5">
              Send messages to all your friends and track how they perform.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <LineOAStoreSwitcher />
            <Button asChild className="bg-[#06C755] hover:bg-[#06C755]/80">
              <Link to="/line-oa/broadcast/new">
                <Plus className="w-4 h-4 mr-1.5" /> New broadcast
              </Link>
            </Button>
          </div>
        </div>

        {/* Quota */}
        <section className="rounded-[8px] border border-black/[0.1] bg-white p-4 flex items-center">
          <span className="rounded-full border border-black/15 p-2.5 mr-3 flex items-center justify-center">
            <Radio className="w-5 h-5 text-[#353a40]" />
          </span>
          <div className="flex-1">
            <div className="flex items-baseline gap-1">
              <span className="text-[1.6rem] font-bold text-[#353a40]">
                {quota ? quota.total_usage.toLocaleString() : '—'}
              </span>
              {quota && quota.quota_type === 'limited' && (
                <span className="text-black/50 text-sm">/ {quota.quota_limit.toLocaleString()}</span>
              )}
            </div>
            <p className="text-xs text-black/50">
              {quota?.quota_type === 'limited' ? 'messages used this month' : 'messages sent this month'}
            </p>
          </div>
        </section>

        {/* Scheduled */}
        {scheduled.length > 0 && (
          <section className="rounded-[8px] border border-black/[0.1] bg-white p-4">
            <h3 className="text-sm font-bold text-[#353a40] mb-3 flex items-center gap-1.5">
              <CalendarClock className="w-4 h-4 text-[#06C755]" /> Scheduled ({scheduled.length})
            </h3>
            <ul className="space-y-2">
              {scheduled.map((s) => (
                <li key={s.name} className="flex items-center gap-3 border-t border-black/5 pt-2 first:border-0 first:pt-0">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                    {s.image_url ? (
                      <img src={s.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Type className="w-4 h-4 text-black/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#353a40] truncate">
                      {s.content_preview || <span className="text-black/40 italic">Image message</span>}
                    </p>
                    <p className="text-[11px] text-[#06C755]">{fmtDateTime(s.scheduled_at)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => cancelScheduled.mutate(s.name)}
                    disabled={cancelScheduled.isPending}
                    className="text-black/40 hover:text-red-500 p-1.5 rounded hover:bg-black/5 shrink-0"
                    title="Cancel"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Past broadcasts */}
        <section className="rounded-[8px] border border-black/[0.1] bg-white p-4">
          <h3 className="text-sm font-bold text-[#353a40] mb-1">Past broadcasts</h3>

          {isError ? (
            <div className="flex items-center gap-3 p-4 rounded bg-red-50 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm flex-1">Couldn&apos;t load broadcast history. Try again.</p>
              <Button variant="ghost" size="sm" onClick={() => refetch()} className="text-red-700 hover:bg-red-100">
                <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
              </Button>
            </div>
          ) : isLoadingHistory ? (
            <div className="space-y-3 mt-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <div className="flex-1 space-y-2"><Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-28" /></div>
                </div>
              ))}
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="py-10 text-center">
              <div className="w-14 h-14 rounded-full bg-[#06C755]/10 flex items-center justify-center mx-auto mb-3">
                <Megaphone className="w-7 h-7 text-[#06C755]" />
              </div>
              <p className="font-medium text-[#353a40]">Send your first broadcast</p>
              <p className="text-sm text-black/50 mt-1 mb-4 max-w-sm mx-auto">
                Reach all your LINE friends with promotions, new arrivals, and announcements.
              </p>
              <Button asChild className="bg-[#06C755] hover:bg-[#06C755]/80">
                <Link to="/line-oa/broadcast/new"><Plus className="w-4 h-4 mr-1.5" /> Send first broadcast</Link>
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm">
                <thead className="bg-[#f8f9fa] text-black/50">
                  <tr>
                    <th className="text-left font-normal py-2 px-3">Past broadcast</th>
                    <th className="text-center font-normal py-2 px-2 w-24">Delivered</th>
                    <th className="text-center font-normal py-2 px-2 w-20">Opens</th>
                    <th className="text-center font-normal py-2 px-2 w-24">Unique clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {broadcasts.map((b, i) => {
                    // line_delivered is a Frappe Int that defaults to 0 (never null), so
                    // `??` never falls back. Use `||` so a genuine broadcast shows its
                    // recipient_count until LINE's insight delivered-count actually lands.
                    const delivered = b.line_delivered || b.recipient_count
                    return (
                      <tr key={b.line_request_id || i} className="border-t border-black/5 hover:bg-black/[0.02]">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                              {b.image_url ? (
                                <img src={b.image_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Megaphone className="w-4 h-4 text-black/30" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[#353a40] leading-snug line-clamp-2 max-w-[300px]">
                                {b.content_preview || <span className="text-black/40 italic">Image message</span>}
                              </p>
                              <p className="text-[11px] text-black/45 mt-0.5">{fmtDateTime(b.sent_at)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-2 text-center text-[#353a40]">
                          {delivered != null ? delivered.toLocaleString() : '—'}
                        </td>
                        <td className="py-2.5 px-2 text-center text-[#353a40]">
                          {b.line_opens != null ? b.line_opens.toLocaleString() : '—'}
                        </td>
                        <td className="py-2.5 px-2 text-center text-[#353a40]">
                          {b.line_unique_clicks != null ? b.line_unique_clicks.toLocaleString() : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-black/40 mt-2 px-1">
                Opens &amp; clicks come from LINE and can take a few hours to appear after sending.
              </p>
            </div>
          )}
        </section>
      </div>
    </PageContainer>
  )
}
